// S18. WHAT THE WORKER APP LAST KNEW, KEPT ON THE PHONE FOR WHEN THERE IS NO SIGNAL.
//
// A worker opens the app in a basement car park to find where to stand. Without this,
// a failed /v1/me shows the disconnected gate, and the shift they were sent is on a
// server they cannot reach. With it, the worker surface renders what it last read,
// marked "offline, as of HH:MM". The server still decides everything on sync: a
// capture made offline is queued (system/offline.ts) and a revoked session answers
// 401 then, with the queue left on the phone.
//
// THREE RULES, each a way this could leak or lie:
//   1. Every entry carries the account it belongs to, and is read back only for that
//      account. Two people sharing a phone never see each other's shifts.
//   2. Sign-out, and any /v1/me that answers "not signed in", forgets everything.
//   3. Only a failure to get an answer falls back: a thrown request, or a non-200 from
//      /v1/me, which means the API is unwell. Signed out is a 200, and it forgets
//      (SessionProvider).

export type KeyValue = {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  key(index: number): string | null;
  readonly length: number;
};

export type Remembered<T> = { account: string; savedAt: string; value: T };

const PREFIX = "verbunk.lastKnown.";

/** localStorage, or null where it is missing or refuses (private mode, a locked-down profile). */
export function browserStore(): KeyValue | null {
  try {
    const kv = globalThis.localStorage;
    const probe = `${PREFIX}probe`;
    kv.setItem(probe, "1");
    kv.removeItem(probe);
    return kv;
  } catch {
    return null;
  }
}

export function remember<T>(kv: KeyValue | null, name: string, account: string, value: T, now = new Date()): void {
  if (kv === null) return;
  const entry: Remembered<T> = { account, savedAt: now.toISOString(), value };
  try {
    kv.setItem(PREFIX + name, JSON.stringify(entry));
  } catch {
    // A full store loses the offline copy, never the online answer.
  }
}

/** The entry, if it exists, parses, and belongs to `account` (any account when omitted). */
export function recall<T>(kv: KeyValue | null, name: string, account?: string): Remembered<T> | null {
  if (kv === null) return null;
  try {
    const raw = kv.getItem(PREFIX + name);
    if (raw === null) return null;
    const entry = JSON.parse(raw) as Remembered<T>;
    if (typeof entry?.account !== "string" || typeof entry.savedAt !== "string") return null;
    if (account !== undefined && entry.account !== account) return null;
    return entry;
  } catch {
    return null;
  }
}

export function forgetAll(kv: KeyValue | null): void {
  if (kv === null) return;
  const mine: string[] = [];
  for (let i = 0; i < kv.length; i += 1) {
    const key = kv.key(i);
    if (key !== null && key.startsWith(PREFIX)) mine.push(key);
  }
  for (const key of mine) kv.removeItem(key);
}

/** The worker surface is the only one that opens offline; the console and portal keep the gate. */
export function isWorkerPath(pathname: string): boolean {
  return pathname === "/app" || pathname.startsWith("/app/");
}
